import React from "react";
import SessionCreditEditor from "./SessionCreditEditor";
import { FaUsers, FaGift, FaPlus, FaSpa } from "react-icons/fa";
import "./SessionManagementTab.css";

/**
 * SessionManagementTab component for managing all session types
 * Organizes session credit editors by category
 */
const SessionManagementTab = ({ customer, formData, onUpdate, errors }) => {
  const getUnifiedMembershipDisplayName = () => {
    const name = String(customer?.sharedMembershipName || "").trim();
    if (name && name.toLowerCase() !== "none" && name !== "Shared Membership") {
      return name;
    }

    const monthlyCredits =
      getSessionData("sharedMembershipSessionUsage")?.totalSessions ||
      getSessionData("sharedMembershipSessionUsage")?.remaining ||
      0;

    if (monthlyCredits === 4) {
      return "Diviti Glow";
    }

    if (monthlyCredits === 8) {
      return "Diviti Radiance";
    }

    if (monthlyCredits >= 12) {
      return "Diviti Elite";
    }

    return "Flexible Session Credits Membership";
  };

  // Helper function to check if a session type has any data
  const hasSessionData = (sessionData) => {
    if (!sessionData) return false;
    const {
      remaining = 0,
      booked = 0,
      completed = 0,
      totalSessions = 0,
      totalCompleted = 0,
    } = sessionData;
    return (
      remaining > 0 ||
      booked > 0 ||
      completed > 0 ||
      totalSessions > 0 ||
      totalCompleted > 0
    );
  };

  // Helper function to get session data with defaults
  const getSessionData = (fieldPath) => {
    const keys = fieldPath.split(".");
    let data = formData;
    for (const key of keys) {
      data = data?.[key];
    }
    return (
      data || {
        remaining: 0,
        booked: 0,
        completed: 0,
        totalSessions: 0,
        totalCompleted: 0,
      }
    );
  };

  // Handle session data updates
  const handleSessionUpdate = (fieldPath, newData) => {
    onUpdate(fieldPath, newData);
  };

  return (
    <div className="session-management-tab">
      <div className="tab-header">
        <h3>Session Credit Management</h3>
        <p className="tab-description">
          Update session counters for all membership types, packages, and
          add-ons. All changes will be logged for audit purposes.
        </p>
      </div>

      {/* Membership Sessions */}
      <div className="session-category">
        <div className="category-header">
          <FaUsers className="category-icon" />
          <h4>Membership Sessions</h4>
        </div>
        <div className="category-content">
          {customer.creditSystemType === "unified_shared" &&
          hasSessionData(getSessionData("sharedMembershipSessionUsage")) ? (
            <SessionCreditEditor
              title={getUnifiedMembershipDisplayName()}
              sessionData={getSessionData("sharedMembershipSessionUsage")}
              onUpdate={handleSessionUpdate}
              errors={errors}
              fieldPrefix="sharedMembershipSessionUsage"
            />
          ) : null}

          {/* Sauna Membership */}
          {(customer.saunaMembershipType &&
            customer.saunaMembershipType !== "none") ||
          hasSessionData(getSessionData("saunaMembershipSessionUsage")) ? (
            <SessionCreditEditor
              title="Sauna Membership"
              sessionData={getSessionData("saunaMembershipSessionUsage")}
              onUpdate={handleSessionUpdate}
              errors={errors}
              fieldPrefix="saunaMembershipSessionUsage"
            />
          ) : (
            <div className="no-session-data">
              <p>No sauna membership sessions</p>
            </div>
          )}

          {/* Red Light Membership */}
          {(customer.redLightMembershipType &&
            customer.redLightMembershipType !== "none") ||
          hasSessionData(getSessionData("redLightMembershipSessionUsage")) ? (
            <SessionCreditEditor
              title="Red Light Membership"
              sessionData={getSessionData("redLightMembershipSessionUsage")}
              onUpdate={handleSessionUpdate}
              errors={errors}
              fieldPrefix="redLightMembershipSessionUsage"
            />
          ) : (
            <div className="no-session-data">
              <p>No red light membership sessions</p>
            </div>
          )}
        </div>
      </div>

      {/* Package Sessions */}
      <div className="session-category">
        <div className="category-header">
          <FaGift className="category-icon" />
          <h4>Package Sessions</h4>
        </div>
        <div className="category-content">
          {(customer.sharedPackageName && customer.sharedPackageName !== "none") ||
          hasSessionData(getSessionData("sharedPackageSessionUsage")) ? (
            <SessionCreditEditor
              title={`Flexible Session Package${
                customer.sharedPackageName && customer.sharedPackageName !== "none"
                  ? ` (${customer.sharedPackageName})`
                  : ""
              }`}
              sessionData={getSessionData("sharedPackageSessionUsage")}
              onUpdate={handleSessionUpdate}
              errors={errors}
              fieldPrefix="sharedPackageSessionUsage"
            />
          ) : (
            <div className="no-session-data">
              <p>No flexible session package credits</p>
            </div>
          )}

          {/* Sauna Package */}
          {(customer.saunaPackageName &&
            customer.saunaPackageName !== "none") ||
          hasSessionData(getSessionData("saunaPackageSessionUsage")) ? (
            <SessionCreditEditor
              title={`Sauna Package${
                customer.saunaPackageName &&
                customer.saunaPackageName !== "none"
                  ? ` (${customer.saunaPackageName})`
                  : ""
              }`}
              sessionData={getSessionData("saunaPackageSessionUsage")}
              onUpdate={handleSessionUpdate}
              errors={errors}
              fieldPrefix="saunaPackageSessionUsage"
            />
          ) : (
            <div className="no-session-data">
              <p>No sauna package sessions</p>
            </div>
          )}

          {/* Red Light Package */}
          {(customer.redLightPackageName &&
            customer.redLightPackageName !== "none") ||
          hasSessionData(getSessionData("redLightPackageSessionUsage")) ? (
            <SessionCreditEditor
              title={`Red Light Package${
                customer.redLightPackageName &&
                customer.redLightPackageName !== "none"
                  ? ` (${customer.redLightPackageName})`
                  : ""
              }`}
              sessionData={getSessionData("redLightPackageSessionUsage")}
              onUpdate={handleSessionUpdate}
              errors={errors}
              fieldPrefix="redLightPackageSessionUsage"
            />
          ) : (
            <div className="no-session-data">
              <p>No red light package sessions</p>
            </div>
          )}
        </div>
      </div>

      {/* Drop-in Credits */}
      <div className="session-category">
        <div className="category-header">
          <FaPlus className="category-icon" />
          <h4>Drop-in Credits</h4>
        </div>
        <div className="category-content">
          {hasSessionData(getSessionData("sharedDropInCount")) ? (
            <SessionCreditEditor
              title="Flexible Session Drop-in Credits"
              sessionData={getSessionData("sharedDropInCount")}
              onUpdate={handleSessionUpdate}
              errors={errors}
              fieldPrefix="sharedDropInCount"
            />
          ) : (
            <div className="no-session-data">
              <p>No flexible session drop-in credits</p>
            </div>
          )}

          {/* Sauna Drop-ins */}
          {hasSessionData(getSessionData("dropInSaunaCount")) ? (
            <SessionCreditEditor
              title="Sauna Drop-in Credits"
              sessionData={getSessionData("dropInSaunaCount")}
              onUpdate={handleSessionUpdate}
              errors={errors}
              fieldPrefix="dropInSaunaCount"
            />
          ) : (
            <div className="no-session-data">
              <p>No sauna drop-in credits</p>
            </div>
          )}

          {/* Red Light Drop-ins */}
          {hasSessionData(getSessionData("dropInRedLightCount")) ? (
            <SessionCreditEditor
              title="Red Light Drop-in Credits"
              sessionData={getSessionData("dropInRedLightCount")}
              onUpdate={handleSessionUpdate}
              errors={errors}
              fieldPrefix="dropInRedLightCount"
            />
          ) : (
            <div className="no-session-data">
              <p>No red light drop-in credits</p>
            </div>
          )}
        </div>
      </div>

      {/* Gifted Credits */}
      <div className="session-category gift-credits-section">
        <div className="category-header">
          <FaGift className="category-icon" />
          <h4>Gifted Credits</h4>
          <span className="gift-badge">Staff Gift</span>
        </div>
        <div className="category-content">
          <SessionCreditEditor
            title="Flexible Session Gifted Credits"
            sessionData={getSessionData("sharedGiftedCredits")}
            onUpdate={handleSessionUpdate}
            errors={errors}
            fieldPrefix="sharedGiftedCredits"
            isGiftCredit={true}
          />

          {/* Gifted Sauna Credits */}
          <SessionCreditEditor
            title="Gifted Sauna Credits"
            sessionData={getSessionData("giftedSaunaCredits")}
            onUpdate={handleSessionUpdate}
            errors={errors}
            fieldPrefix="giftedSaunaCredits"
            isGiftCredit={true}
          />

          {/* Gifted Red Light Credits */}
          <SessionCreditEditor
            title="Gifted Red Light Credits"
            sessionData={getSessionData("giftedRedLightCredits")}
            onUpdate={handleSessionUpdate}
            errors={errors}
            fieldPrefix="giftedRedLightCredits"
            isGiftCredit={true}
          />
        </div>
      </div>

      {/* Add-on Entitlements */}
      <div className="session-category">
        <div className="category-header">
          <FaSpa className="category-icon" />
          <h4>Add-on Entitlements</h4>
        </div>
        <div className="category-content">
          {/* Halotherapy */}
          {hasSessionData(getSessionData("halotherapyEntitlement")) ? (
            <SessionCreditEditor
              title="Halotherapy Entitlement"
              sessionData={getSessionData("halotherapyEntitlement")}
              onUpdate={handleSessionUpdate}
              errors={errors}
              fieldPrefix="halotherapyEntitlement"
            />
          ) : (
            <div className="no-session-data">
              <p>No halotherapy entitlements</p>
            </div>
          )}

          {/* Guest Pass Entitlement */}
          {hasSessionData(getSessionData("guestPassEntitlement")) ? (
            <SessionCreditEditor
              title="Guest Pass Entitlement"
              sessionData={getSessionData("guestPassEntitlement")}
              onUpdate={handleSessionUpdate}
              errors={errors}
              fieldPrefix="guestPassEntitlement"
            />
          ) : (
            <div className="no-session-data">
              <p>No guest pass entitlements</p>
            </div>
          )}

          {/* Member Gift Session Entitlement */}
          {hasSessionData(getSessionData("memberGiftSessionEntitlement")) ? (
            <SessionCreditEditor
              title="Member Gift Session Entitlement"
              sessionData={getSessionData("memberGiftSessionEntitlement")}
              onUpdate={handleSessionUpdate}
              errors={errors}
              fieldPrefix="memberGiftSessionEntitlement"
            />
          ) : (
            <div className="no-session-data">
              <p>No member gift session entitlements</p>
            </div>
          )}
        </div>
      </div>

      {/* Summary Information */}
      <div className="session-summary">
        <h4>Summary</h4>
        <div className="summary-grid">
          <div className="summary-item">
            <span className="summary-label">Total Active Sessions:</span>
            <span className="summary-value">
              {Object.keys(formData).reduce((total, key) => {
                if (
                  key.includes("SessionUsage") ||
                  key.includes("Count") ||
                  key.includes("Entitlement") ||
                  key.includes("Credits")
                ) {
                  const data = formData[key];
                  return total + (data?.remaining || 0) + (data?.booked || 0);
                }
                return total;
              }, 0)}
            </span>
          </div>
          <div className="summary-item">
            <span className="summary-label">Total Completed (Lifetime):</span>
            <span className="summary-value">
              {Object.keys(formData).reduce((total, key) => {
                if (
                  key.includes("SessionUsage") ||
                  key.includes("Count") ||
                  key.includes("Entitlement") ||
                  key.includes("Credits")
                ) {
                  const data = formData[key];
                  return total + (data?.totalCompleted || 0);
                }
                return total;
              }, 0)}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SessionManagementTab;
